import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import { TimestampProvider } from "@repo/ui/timestamps/timestampProvider";
import { useRef, type FC } from "react";
import { Outlet, useLocation, type RouteObject } from "react-router";
import { RouteErrorBoundary } from "../components/routeErrorBoundary.js";
import { CopilotProvider } from "../features/copilot/copilotProvider.js";
import { FilterProvider } from "../features/filters/filterProvider.js";
import { GlobalKeyboardShortcutsProvider } from "../features/keyboardShortcuts/globalKeyboardShortcuts.js";
import { NotificationsOverlay } from "../features/notificationsOverlay/notificationsOverlay.js";
import { AppSearchProvider } from "../features/search/appSearchProvider.js";
import { getIsInFountainIntegration } from "../helpers/utilities.js";
import { AppLayout } from "./appLayout.js";
import { AppNavigationContainer } from "./appNavigationContainer.js";
import { AppBreadcrumbsContainer } from "./navigation/breadcrumbs.js";

/** Builds the production route hierarchy for browser and memory routers. */
export function createAdminRouteObjects(
  children: RouteObject[],
): RouteObject[] {
  return [
    {
      element: <AdminLayout />,
      ErrorBoundary: RouteErrorBoundary,
      children,
    },
  ];
}

const AdminLayout: FC = () => {
  const globalElementRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // A single interview embedded in Fountain gets no app chrome at all: just the interview slideover.
  const isShowingASingleInterviewInFountain =
    pathname.includes(`/${ProjectDetailsTabPath.Interviews}/`) &&
    getIsInFountainIntegration();

  const content = (
    <FilterProvider>
      <Outlet />
    </FilterProvider>
  );

  // TODO: Use document flow instead of nested scrollable containers to simplify layout
  return (
    <GlobalKeyboardShortcutsProvider elementRef={globalElementRef}>
      <TimestampProvider>
        <CopilotProvider>
          <AppSearchProvider>
            {isShowingASingleInterviewInFountain ? (
              <div
                ref={globalElementRef}
                className="relative h-full overflow-hidden"
              >
                {content}
              </div>
            ) : (
              <AppLayout
                ref={globalElementRef}
                navigation={<AppNavigationContainer />}
                breadcrumbs={<AppBreadcrumbsContainer />}
              >
                {content}
              </AppLayout>
            )}
          </AppSearchProvider>
          <NotificationsOverlay />
        </CopilotProvider>
      </TimestampProvider>
    </GlobalKeyboardShortcutsProvider>
  );
};
